import { Box, Button, CircularProgress } from '@mui/material'
import { EarnestMoneyDialog } from '../../dialogs/EarnestMoneyDialog'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useEarnestMoneyFlow } from '../../../../logic/useEarnestMoneyFlow'
import { DownloadUploadsComponent } from '../../../DownloadUploadsComponent'
import { useContent } from '../../../../logic'
import { toTitleCase } from '../../../../utils'
import { Alert } from '@mui/material'
import { useGetNotification, useGetOrder } from '../../../../logic/useApiQuery'
import { EarnestMoneyContactDialog, ContactInfo } from './EarnestMoneyContactDialog'

export function EarnestMoneyTask({ orderKey, notificationId }: {
  orderKey: string, notificationId: string,
}) {
  const { search } = useLocation()
  const { data: order } = useGetOrder(orderKey, search)
  const { data: notification } = useGetNotification()

  const isCommercial = notification?.Configuration?.IsCommercial ?? false
  const userMappingInfo = order?.userMappingInfo
  const needsContactInfo = isCommercial ? !userMappingInfo?.entityName : !(userMappingInfo?.firstName && userMappingInfo?.lastName)

  const [showEarnestMoneyDialog, setShowEarnestMoneyDialog] = useState(false)
  const [showContactDialog, setShowContactDialog] = useState(false)
  const initialContactInfo: ContactInfo | undefined = !needsContactInfo
    ? isCommercial && userMappingInfo?.entityName
      ? { entityName: userMappingInfo.entityName, isCommercial: true }
      : !isCommercial && userMappingInfo?.firstName && userMappingInfo?.lastName
        ? { firstName: userMappingInfo.firstName, lastName: userMappingInfo.lastName, isCommercial: false }
        : undefined
    : undefined
  const [contactInfo, setContactInfo] = useState<ContactInfo | undefined>(initialContactInfo)

  const earnestMoneyDialogClicked = async () => {
    if (needsContactInfo && !contactInfo) {
      setShowContactDialog(true)
    } else {
      setShowEarnestMoneyDialog(true)
    }
  }

  const handleContactSubmit = (info: ContactInfo) => {
    setContactInfo(info)
    localStorage.removeItem('$activeInteraction')
    localStorage.removeItem('$k')
    initiatePaymentMutation.mutate({
      isCommercial: info.isCommercial,
      ...(info.isCommercial === false && { firstName: info.firstName, lastName: info.lastName }),
      ...(info.isCommercial === true && { entityName: info.entityName }),
    }, {
      onSuccess: () => {
        setShowContactDialog(false)
        setShowEarnestMoneyDialog(true)
      }
    });
  }

  const { TaskText } = useContent()

  const {
    initiatePaymentMutation,
    cachedInitiatePaymentData,
    isPaymentSubmitted,
    isPaymentAcknowledged,
    isPaymentCompleted,
  } = useEarnestMoneyFlow(orderKey, notificationId, search, contactInfo, needsContactInfo)

  const initiatePaymentData = cachedInitiatePaymentData || initiatePaymentMutation?.data

  const showDownloadUploadsButton = isPaymentSubmitted || isPaymentAcknowledged || isPaymentCompleted

  return (
    <>
      {isPaymentCompleted ? (
        <></>  // Handled by PortalTaskComponent.  Fix later.
      ) : isPaymentAcknowledged || isPaymentSubmitted ? (
        <Box sx={{ textAlign: 'left', alignItems: 'flex-start', width: '100%' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your earnest money payment has been initiated, and your receipt is available below. Once the title company approves and processes your payment, the funds will be debited from your account.
          </Alert>
        </Box>
      ) : !isPaymentSubmitted && !initiatePaymentMutation?.error ? (
        <Button
          variant="contained"
          color="primary"
          onClick={earnestMoneyDialogClicked}
          startIcon={!initiatePaymentData && (!needsContactInfo || !!contactInfo) ? <CircularProgress size={20} /> : null}
          disabled={!initiatePaymentData && (!needsContactInfo || !!contactInfo)}
        >
          {!initiatePaymentData && (!needsContactInfo || !!contactInfo) ? 'Loading...' : 'Complete Earnest Money'}
        </Button>
      ) : initiatePaymentMutation?.error ? (
        <Box sx={{ textAlign: 'left', alignItems: 'flex-start', width: '100%' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {initiatePaymentMutation.error.message}
          </Alert>
        </Box>
      ) : null}
      {
        showDownloadUploadsButton &&
        <DownloadUploadsComponent
          button={
            <Button
              variant="contained"
              sx={{
                borderRadius: "40px",
                minWidth: "250px",
                color: "white",
              }}
            >
              {`Download ${toTitleCase(TaskText)} Documents`}
            </Button>
          }
        />
      }
      {
        showEarnestMoneyDialog &&
        initiatePaymentData &&
        <EarnestMoneyDialog
          regCode={initiatePaymentData.Registration}
          interactionPrefix={initiatePaymentData.InitiateEMDResponse.interactionPrefix}
          interactionSuffix={initiatePaymentData.InitiateEMDResponse.interactionSuffix}
          onClosed={() => { setShowEarnestMoneyDialog(false) }}
        />
      }
      <EarnestMoneyContactDialog
        open={showContactDialog}
        loading={initiatePaymentMutation.isLoading}
        onSubmit={handleContactSubmit}
        onClose={() => setShowContactDialog(false)}
      />
    </>
  )
}
